import { isGrainFixed, isPanelRotationAllowed } from "@plaatplan/core";
import { useMemo } from "react";
import { useLocale } from "../i18n/useLocale.js";
import { LengthInput } from "./LengthInput";
import { ThicknessField } from "./ThicknessField";
import {
  getCombinedMaterialPresetIds,
  getCombinedPresets,
  isActiveCombinedPreset,
  resolveCombinedPreset,
} from "./combinedPresets";
import {
  getMoreMaterialPresets,
  getPrimaryMaterialPresets,
  isActiveMaterialPreset,
  resolveMaterialPreset,
} from "./materialPresets";
import {
  getMoreSheetPresets,
  isActiveSheetPreset,
  resolveSheetPreset,
} from "./sheetPresets";

const PRIMARY_QUICK_PRESET_COUNT = 2;

function PresetChip({ preset, selected, onClick, showHint = true }) {
  return (
    <button
      type="button"
      className={`preset-chip${selected ? " selected" : ""}`}
      onClick={onClick}
      title={preset.hint || preset.materialName || preset.label}
      aria-pressed={selected}
    >
      <strong>{preset.label}</strong>
      {showHint && preset.hint && <small>{preset.hint}</small>}
    </button>
  );
}

export function StandaardplaatCard({
  project,
  panelThicknesses,
  defaultThicknessPresets,
  onUpdateSource,
  onApplyCombinedPreset,
  onApplyMaterialPreset,
  onSetPanelRotationAllowed,
  onSetGrainFixed,
  onApplySheetPreset,
}) {
  const { display } = useLocale();
  const rotationAllowed = isPanelRotationAllowed(project.sourcePanel);
  const grainFixed = isGrainFixed(project.sourcePanel);
  const combinedPresets = getCombinedPresets().map((preset) => resolveCombinedPreset(preset, display));
  const primaryQuickPresets = combinedPresets.slice(0, PRIMARY_QUICK_PRESET_COUNT);
  const moreQuickPresets = combinedPresets.slice(PRIMARY_QUICK_PRESET_COUNT);
  const combinedMaterialIds = useMemo(() => new Set(getCombinedMaterialPresetIds()), []);
  const extraMaterialPresets = useMemo(
    () => [...getPrimaryMaterialPresets(), ...getMoreMaterialPresets()]
      .map((preset) => resolveMaterialPreset(preset, display))
      .filter((preset) => !combinedMaterialIds.has(preset.id)),
    [combinedMaterialIds, display],
  );
  const moreSheets = getMoreSheetPresets().map((preset) => resolveSheetPreset(preset, display));
  const quickThicknessPresets = defaultThicknessPresets ?? panelThicknesses;
  const dimensionUnit = display.unitSystem === "imperial"
    ? display.t("web.fields.unitIn")
    : display.t("web.fields.unitMm");
  const moreQuickActive = moreQuickPresets.some((preset) => isActiveCombinedPreset(project, preset));
  const extraMaterialActive = extraMaterialPresets.some(
    (preset) => isActiveMaterialPreset(project, preset, preset.materialName),
  );
  const moreMaterialsOpen = moreQuickActive || extraMaterialActive;

  return (
    <section className="rail-card stock-card">
      <div className="card-kicker">{display.t("web.sections.materialStock")}</div>
      <h2>{display.t("web.sections.standardSheet")}</h2>
      <div className="source-form">
        <div
          className="source-field-group source-field-group-first"
          role="group"
          aria-label={display.t("web.source.quickPresetsLegend")}
        >
          <div className="source-field-group-heading">
            {display.t("web.source.quickPresetsLegend")}
          </div>
          <p className="source-group-hint source-group-hint-first">
            {display.t("web.source.quickPresetsHint")}
          </p>
          <div className="preset-quick-stack">
            <div className="preset-grid preset-grid--2">
              {primaryQuickPresets.map((preset) => (
                <PresetChip
                  key={preset.id}
                  preset={preset}
                  selected={isActiveCombinedPreset(project, preset)}
                  onClick={() => onApplyCombinedPreset(preset)}
                />
              ))}
            </div>
            <details
              className="source-material-more"
              {...(moreMaterialsOpen ? { open: true } : {})}
            >
              <summary>{display.t("web.source.moreMaterials")}</summary>
              {moreQuickPresets.length > 0 ? (
                <div className="preset-grid preset-grid--2">
                  {moreQuickPresets.map((preset) => (
                    <PresetChip
                      key={preset.id}
                      preset={preset}
                      selected={isActiveCombinedPreset(project, preset)}
                      onClick={() => onApplyCombinedPreset(preset)}
                    />
                  ))}
                </div>
              ) : null}
              {extraMaterialPresets.length > 0 ? (
                <div className="preset-grid preset-grid--2">
                  {extraMaterialPresets.map((preset) => (
                    <PresetChip
                      key={preset.id}
                      preset={preset}
                      selected={isActiveMaterialPreset(project, preset, preset.materialName)}
                      onClick={() => onApplyMaterialPreset(preset)}
                    />
                  ))}
                </div>
              ) : null}
            </details>
          </div>
        </div>

        <div
          className="source-field-group"
          role="group"
          aria-label={display.t("web.source.sheetSizeLegend")}
        >
          <div className="source-field-group-heading">
            {display.t("web.source.sheetSizeLegend")}
          </div>

          <div className="source-dimension-row">
            <label>
              <span>{display.t("web.fields.length")}</span>
              <div className="number-field">
                <LengthInput
                  className="table-input dimension-input"
                  valueMm={project.sourcePanel.length}
                  onChangeMm={(value) => onUpdateSource("length", value)}
                />
                <em>{dimensionUnit}</em>
              </div>
            </label>
            <span className="source-dimension-separator" aria-hidden="true">×</span>
            <label>
              <span>{display.t("web.fields.width")}</span>
              <div className="number-field">
                <LengthInput
                  className="table-input dimension-input"
                  valueMm={project.sourcePanel.width}
                  onChangeMm={(value) => onUpdateSource("width", value)}
                />
                <em>{dimensionUnit}</em>
              </div>
            </label>
          </div>

          <ThicknessField
            variant="card"
            valueMm={project.sourcePanel.defaultThickness}
            presets={panelThicknesses}
            quickPresets={quickThicknessPresets}
            ariaLabel={display.t("web.source.defaultThicknessLabel")}
            presetsAriaLabel={display.t("web.source.defaultThicknessPresets")}
            onChangeMm={(value) => onUpdateSource("defaultThickness", value)}
          />

          {moreSheets.length > 0 && (
            <details className="source-material-more">
              <summary>{display.t("web.source.moreStandardFormats")}</summary>
              <div className="preset-grid preset-grid--2">
                {moreSheets.map((preset) => (
                  <PresetChip
                    key={preset.id}
                    preset={preset}
                    selected={isActiveSheetPreset(project.sourcePanel, preset)}
                    onClick={() => onApplySheetPreset(preset)}
                  />
                ))}
              </div>
            </details>
          )}
        </div>

        <div
          className="source-field-group"
          role="group"
          aria-label={display.t("web.source.sawGrainLegend")}
        >
          <div className="source-field-group-heading">
            {display.t("web.source.sawGrainLegend")}
          </div>
          <div className="dimension-pair">
            <label>
              <span>{display.t("web.source.kerfLabel")}</span>
              <div className="number-field compact">
                <LengthInput
                  compact
                  className="table-input dimension-input"
                  valueMm={project.sourcePanel.kerf}
                  minMm={0}
                  onChangeMm={(value) => onUpdateSource("kerf", value)}
                />
                <em>{dimensionUnit}</em>
              </div>
            </label>
            <label>
              <span>{display.t("web.source.marginLabel")}</span>
              <div className="number-field compact">
                <LengthInput
                  compact
                  className="table-input dimension-input"
                  valueMm={project.sourcePanel.margin}
                  minMm={0}
                  onChangeMm={(value) => onUpdateSource("margin", value)}
                />
                <em>{dimensionUnit}</em>
              </div>
            </label>
            <p className="field-hint field-hint--span">
              {display.t("web.source.kerfHint")}
            </p>
          </div>

          <div className="source-grain-panel">
            <label>
              <span>{display.t("web.source.grainLabel")}</span>
              <select
                value={grainFixed ? "fixed" : "none"}
                onChange={(event) => onSetGrainFixed(event.target.value === "fixed")}
              >
                <option value="none">{display.t("web.source.grainNone")}</option>
                <option value="fixed">{display.t("web.source.grainFixedOption")}</option>
              </select>
            </label>
            <p className="source-group-hint">
              {grainFixed
                ? display.t("web.source.grainFixedHint")
                : display.t("web.source.grainFreeHint")}
            </p>
            <label className={`toggle${grainFixed ? " toggle--disabled" : ""}`}>
              <input
                type="checkbox"
                checked={rotationAllowed}
                disabled={grainFixed}
                onChange={(event) => onSetPanelRotationAllowed(event.target.checked)}
              />
              <span>{display.t("web.source.rotationLabel")}</span>
            </label>
          </div>
        </div>
      </div>
    </section>
  );
}
